import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalProps,
  SelectDropdownInput,
  TextboxInput,
  Pill,
  HttpAutocomplete,
  OptionType,
  EmptyBlockContent,
} from '@tradingblock/components';
import { Modal as BootstrapModal, Button, Card } from 'react-bootstrap';
import { useStateSelector } from '../../data/global/dataSelectors';
import { useBlockId } from '../../components/blocks/BlockState';
import { SymbolChannels } from '../../types';
import { useDispatch } from 'react-redux';
import { InfoDataActions } from '../../data/global/actions/InfoActions';
import { SymbolToOption, useSymbolSearch } from '../../hooks/useSymbolSearch';
import { AssetSymbol } from '@tradingblock/types';
import { useInfoSettings } from './hooks/useInfoSettings';
import _ from 'lodash';

export interface NewsSubscriptionModalProps {
  setShow: ModalProps['setShow'];
  show: ModalProps['show'];
  removeNews: (symbol: string) => void;
}

export default function NewsSubscriptionModal({ show, setShow, removeNews }: NewsSubscriptionModalProps) {
  const dispatch = useDispatch();
  const blockId = useBlockId();
  const subRequests = useStateSelector(s => s.information.marketNews[blockId]);
  const [selectedSymbol, setSelectedSymbol] = useState<SymbolChannels>();
  const [errorState, setErrorState] = useState<string>('');
  const [addMulti, setAddMulti] = useState<boolean>(false);
  const [addMultiSymbols, setAddMultiSymbols] = useState<string[]>([]);
  const [addMultiChannels, setAddMultiChannels] = useState<string[]>([]);
  const [addMultiErrorState, setAddMultiErrorState] = useState<string>('');
  const [symbol, setSymbol] = useState<AssetSymbol | undefined>();
  const symbolValue = symbol ? symbol.symbol : '';
  const [symbolChannelsPayload, setSymbolChannelsPayload] = useState<SymbolChannels[]>(subRequests.symbolRequest);
  const search = useSymbolSearch();
  const settings = useInfoSettings();

  const settingChannelMappings = [
    { active: settings.dailyNewsfeed, name: 'DailyNewsfeed' },
    { active: settings.corporateActionsDividends, name: 'CorporateActionsDividends' },
    { active: settings.earningsReports, name: 'EarningsReports' },
    { active: settings.rumorsAndIdeas, name: 'RumorsAndIdeas' },
    { active: settings.companyNews, name: 'CompanyNews' },
    { active: settings.sectorNews, name: 'SectorNews' },
    { active: settings.ipos, name: 'Ipos' },
    { active: settings.economicsAndGovernment, name: 'EconomicsAndGovernment' },
    { active: settings.theMarkets, name: 'TheMarkets' },
    { active: settings.etfCoverage, name: 'EtfCoverage' },
    { active: settings.options, name: 'Options' },
  ];

  const channelArray: string[] = [];
  // Populate channelArray for MarketNews polling request based on useInfoSettings()
  if (_.isEmpty(settings)) {
    channelArray.push('DailyNewsfeed');
  } else {
    settingChannelMappings.forEach(channelObj => {
      if (channelObj.active) channelArray.push(channelObj.name);
    });
  }

  const currentFilters = useMemo(() => {
    return subRequests && subRequests.filteredNewsSymbols;
  }, [subRequests && subRequests.filteredNewsSymbols]);

  const isDJIA = symbolValue === '$DJIA' || symbolValue === '$DJI';
  const isSPX = symbolValue === '$SPX';
  const isNasdaq = symbolValue.includes('$NDX');
  const isVIX = symbolValue === '$VIX';
  const isUnsupportedIndex = !isDJIA && !isSPX && !isNasdaq && !isVIX && symbolValue && symbolValue[0] === '$';

  const autoCompleteValue = useMemo(() => {
    return symbol ? SymbolToOption(symbol, true) : undefined;
  }, [symbol]);

  useEffect(() => {
    if (symbolValue)
      if (addMulti) {
        if (
          symbolChannelsPayload &&
          !isUnsupportedIndex &&
          !symbolChannelsPayload.find(s => s.name === symbolValue) &&
          !addMultiSymbols.find(s => s === symbolValue)
        ) {
          setAddMultiErrorState('');
          handleAddSymbol(symbolValue);
        }
      } else {
        if (symbolChannelsPayload && !isUnsupportedIndex && !symbolChannelsPayload.find(s => s.name === symbolValue)) {
          handleAddNews(symbolValue);
          handleAddFilter(symbolValue);
        }
      }
  }, [symbolValue, addMulti, symbolChannelsPayload]);

  const onSelect = useCallback(
    (value: OptionType) => {
      const symbolName = value ? value.data : undefined;
      if (symbolName) {
        setSymbol(symbolName);
      }
      if (!symbolName) {
        setSymbol(null!);
      }
    },
    [setSymbol]
  );

  const handleAddSymbol = useCallback(
    (symbol: string) => {
      if (symbol) {
        if (symbolChannelsPayload.find(s => s.name === symbol))
          setAddMultiErrorState(symbol + ' is already subscribed.');
        else if (addMultiSymbols.find(s => s === symbol))
          setAddMultiErrorState(symbol + ' is already added to the list.');
        else setAddMultiSymbols([...addMultiSymbols, symbol]);
      }
    },
    [symbolChannelsPayload, addMultiSymbols, setAddMultiErrorState]
  );

  const handleRemoveSymbol = (symbol: string) => {
    if (symbol) setAddMultiSymbols(addMultiSymbols.filter(s => s !== symbol));
  };

  const handleAddNews = (symbol: string) => {
    const addSymbol: SymbolChannels = {
      name: symbol,
      channels: channelArray,
    };
    const payload = [...symbolChannelsPayload, addSymbol];

    dispatch(InfoDataActions.editMarketNewsBlock({ blockId: blockId, symbols: payload }));
    setSymbolChannelsPayload(payload);
  };

  const handleAddFilter = (symbol: string) => {
    if (currentFilters && currentFilters.length > 0)
      dispatch(InfoDataActions.filterMarketNewsBlock({ blockId: blockId, symbols: [...currentFilters, symbol] }));
    else dispatch(InfoDataActions.filterMarketNewsBlock({ blockId: blockId, symbols: [symbol] }));
  };

  const handleRemoveFilter = (symbol: string) => {
    if (currentFilters && currentFilters.length === 1)
      dispatch(InfoDataActions.filterMarketNewsBlock({ blockId: blockId, symbols: [] }));
    else if (currentFilters && currentFilters.length > 0)
      dispatch(
        InfoDataActions.filterMarketNewsBlock({
          blockId: blockId,
          symbols: [...currentFilters.filter(s => s !== symbol)],
        })
      );
  };

  const handleClearFilter = () => {
    if (currentFilters && currentFilters.length > 0)
      dispatch(InfoDataActions.filterMarketNewsBlock({ blockId: blockId, symbols: [] }));
  };

  const handleDelete = (symbol: string) => {
    setSymbolChannelsPayload(symbolChannelsPayload.filter(s => s.name !== symbol));
    setSymbol(null!);
    removeNews(symbol);
  };

  const handleSaveSelectedSymbol = (symbol: SymbolChannels) => {
    if (symbol.channels.length > 0) {
      const payload = symbolChannelsPayload.map(s => (s.name === symbol.name ? symbol : s));
      setSymbolChannelsPayload(payload);
      setSelectedSymbol(undefined);
      dispatch(
        InfoDataActions.editMarketNewsBlock({
          blockId: blockId,
          symbols: [...payload],
        })
      );
    } else {
      setErrorState('Must have at least one channel selected!');
    }
  };

  const addSpaceBeforeCaps = (str: string) => {
    return str.replace(/(?<=[A-Z][a-z]*)(?=[A-Z])/g, ' ');
  };

  const channels = [
    'DailyNewsfeed',
    'CorporateActionsDividends',
    'EarningsReports',
    'RumorsAndIdeas',
    'CompanyNews',
    'SectorNews',
    'Ipos',
    'EconomicsAndGovernment',
    'TheMarkets',
    'EtfCoverage',
    'Options',
  ];

  const channelMappings = channels.map(c => {
    return {
      label: addSpaceBeforeCaps(c),
      raw_value: c,
      value: c,
    };
  });

  const handleSubmitMulti = () => {
    if (addMultiSymbols.length > 0 && addMultiChannels.length > 0) {
      const mergePayload: SymbolChannels[] = addMultiSymbols.map(s => {
        return {
          name: s,
          channels: addMultiChannels,
        };
      });
      const payload = [...symbolChannelsPayload, ...mergePayload];
      dispatch(
        InfoDataActions.editMarketNewsBlock({
          blockId: blockId,
          symbols: [...payload],
        })
      );
      setSymbolChannelsPayload(payload);
      setSymbol(null!);
      setAddMultiSymbols([]);
      setAddMultiChannels([]);
      setAddMulti(false);
    } else {
      setAddMultiErrorState('Must have at least one symbol/channel added!');
    }
  };

  const HoverCard = ({ s, ind }: { s: SymbolChannels; ind: number }) => {
    const [isHovered, setIsHovered] = useState(false);

    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);

    return (
      <Card
        className={ind !== symbolChannelsPayload.length - 1 ? 'mb-20px' : ''}
        style={{ backgroundColor: `${isHovered ? 'var(--lighten2x)' : 'var(--lighten)'}` }}
      >
        <Card.Header>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <div>{s.name}</div>
            <div>
              {currentFilters && !currentFilters.includes(s.name) && (
                <button
                  style={{
                    paddingRight: '10px',
                  }}
                  title="Add to Filter"
                  onClick={() => handleAddFilter(s.name)}
                >
                  <i className="fal fa-filter"></i>
                </button>
              )}
              {currentFilters && currentFilters.includes(s.name) && (
                <button
                  style={{
                    paddingRight: '10px',
                  }}
                  title="Remove from Filter"
                  onClick={() => handleRemoveFilter(s.name)}
                >
                  <i className="fas fa-filter"></i>
                </button>
              )}
              <button title="Remove News Subscription" onClick={() => handleDelete(s.name)}>
                <i className="fas fa-trash"></i>
              </button>
            </div>
          </div>
        </Card.Header>
        <Card.Body
          title={`Edit News Subscription for ${s.name}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{ cursor: 'pointer', backgroundColor: `${isHovered ? 'var(--lighten)' : ''}` }}
          onClick={() => {
            setSelectedSymbol(s);
          }}
        >
          {s.channels.map((c, i) => (
            <span key={i} className="badge badge-secondary m-lr-5px">
              {c}
            </span>
          ))}
        </Card.Body>
      </Card>
    );
  };

  return (
    <Modal size="lg" setShow={setShow} show={show} className="remove-position">
      <BootstrapModal.Header closeButton>
        <BootstrapModal.Title>Manage News</BootstrapModal.Title>
      </BootstrapModal.Header>
      <BootstrapModal.Body>
        {!addMulti && (!symbolChannelsPayload || (symbolChannelsPayload && symbolChannelsPayload.length === 0)) && (
          <EmptyBlockContent>Please Add New Symbols.</EmptyBlockContent>
        )}
        {!selectedSymbol && !addMulti && symbolChannelsPayload && symbolChannelsPayload.length > 0 && (
          <>
            <div style={{ paddingBottom: '20px' }}>
              <div className="block-nav nav">
                <div className="nav-item">
                  <Pill>
                    <HttpAutocomplete
                      minimumLength={1}
                      search={(value: string, limit?: number | undefined) => {
                        return search(value, limit);
                      }}
                      value={autoCompleteValue}
                      onSelect={onSelect}
                      clearOptionsOnSelect={true}
                      placeholder="Search..."
                      symbol={symbolValue || undefined}
                      charLimit={20}
                    />
                  </Pill>
                </div>
                {symbol && (
                  <>
                    <div className="nav-item nav-stock-name">
                      <div className="txt-sm">
                        <strong>{symbol.symbol}</strong>
                      </div>
                      <div className="txt-sm mute" title={symbol.name}>
                        {symbol.name}
                      </div>
                    </div>
                  </>
                )}
                {currentFilters && currentFilters.length > 0 && (
                  <Button
                    title={`Clear Filter`}
                    variant="secondary"
                    style={{ borderRadius: '5px' }}
                    onClick={handleClearFilter}
                  >
                    Clear Filter
                  </Button>
                )}
              </div>
            </div>
            {symbolChannelsPayload.map((s, ind) => (
              <HoverCard key={ind} s={s} ind={ind} />
            ))}
          </>
        )}
        {selectedSymbol && (
          <>
            <TextboxInput value={selectedSymbol.name} label="Symbol" disabled />
            <SelectDropdownInput
              label="Channels"
              value={selectedSymbol.channels}
              options={channelMappings.filter(c => !selectedSymbol.channels.includes(c.raw_value))}
              onchange={(e, values: string[]) => {
                setSelectedSymbol({
                  name: selectedSymbol.name,
                  channels: [...values],
                });
                setErrorState('');
              }}
              isMulti
            />
            {errorState && <div className="warn">{errorState}</div>}
          </>
        )}
        {addMulti && (
          <>
            <div style={{ paddingBottom: '20px' }}>
              <div className="block-nav nav">
                <div className="nav-item">
                  <Pill>
                    <HttpAutocomplete
                      minimumLength={1}
                      search={(value: string, limit?: number | undefined) => {
                        return search(value, limit);
                      }}
                      value={autoCompleteValue}
                      onSelect={onSelect}
                      clearOptionsOnSelect={true}
                      placeholder="Search..."
                      symbol={symbolValue || undefined}
                      charLimit={20}
                    />
                  </Pill>
                </div>
                {symbol && (
                  <>
                    <div className="nav-item nav-stock-name">
                      <div className="txt-sm">
                        <strong>{symbol.symbol}</strong>
                      </div>
                      <div className="txt-sm mute" title={symbol.name}>
                        {symbol.name}
                      </div>
                    </div>
                    {addMultiSymbols && addMultiSymbols.find(s => s === symbolValue) && (
                      <div className="nav-item">
                        <button
                          title="Remove News Subscription"
                          type="button"
                          onClick={() => {
                            setAddMultiErrorState('');
                            handleRemoveSymbol(symbolValue);
                          }}
                        >
                          <i className="fas fa-minus-square btn-put"></i>
                        </button>
                      </div>
                    )}
                  </>
                )}
                {addMultiSymbols && addMultiSymbols.length > 0 && (
                  <Button
                    title={`Clear News Subscriptions`}
                    variant="secondary"
                    style={{ borderRadius: '5px' }}
                    onClick={() => {
                      setAddMultiErrorState('');
                      setAddMultiSymbols([]);
                      setAddMultiChannels([]);
                      setSymbol(null!);
                    }}
                  >
                    Clear All
                  </Button>
                )}
              </div>
            </div>
            <TextboxInput
              value={
                symbolChannelsPayload && symbolChannelsPayload.length > 0
                  ? symbolChannelsPayload.map(s => s.name).join(', ')
                  : ''
              }
              label="Subscribed Symbols"
              disabled
            />
            <TextboxInput value={addMultiSymbols.join(', ')} label="New Symbols To Subscribe" disabled />
            <SelectDropdownInput
              label="Channels To Apply To New Symbols"
              value={addMultiChannels}
              options={channelMappings.filter(c => !addMultiChannels.includes(c.raw_value))}
              onchange={(e, values: string[]) => {
                setAddMultiChannels([...values]);
                setAddMultiErrorState('');
              }}
              isMulti
            />
            {addMultiErrorState && <div className="warn">{addMultiErrorState}</div>}
          </>
        )}
      </BootstrapModal.Body>
      {selectedSymbol && (
        <BootstrapModal.Footer>
          <Button variant="secondary" onClick={() => setSelectedSymbol(undefined)}>
            Back
          </Button>
          <Button variant="primary" onClick={() => handleSaveSelectedSymbol(selectedSymbol)}>
            Update
          </Button>
        </BootstrapModal.Footer>
      )}
      {!selectedSymbol && !addMulti && (
        <BootstrapModal.Footer>
          <Button variant="secondary" style={{ marginRight: 'auto' }} onClick={() => setAddMulti(true)}>
            Add Multiple Symbols
          </Button>
          <Button variant="secondary" onClick={() => setShow(false)}>
            Close
          </Button>
        </BootstrapModal.Footer>
      )}
      {addMulti && (
        <BootstrapModal.Footer>
          <Button variant="secondary" onClick={() => setAddMulti(false)}>
            Back
          </Button>
          <Button variant="primary" onClick={handleSubmitMulti}>
            Add All
          </Button>
        </BootstrapModal.Footer>
      )}
    </Modal>
  );
}
